import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "antd";

import { ASSESSMENT_EDIT_INVITES_ROUTE, ASSESSMENT_ROUTE, CLIENT_ROUTE } from "@/routing/AppRouter/routes.constants";

import { AssessmentHttpService } from "@/modules/assessments/services/assessment-http.service";
import { CandidateUtils } from "@/modules/assessments/scenes/AddCandidates/candidate.utils";

import { AddCandidateForm } from "@/shared/components/AddNew/AddCandidateForm";

import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { AssessmentAbilityContext } from "@/shared/contexts/ability.context";
import { AssessmentContext } from "@/shared/contexts/assessment.context";
import { useValidateClientAccessHook } from "@/shared/hooks/access/useValidateClientAccess.hook";

import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import { DEFAULT_LOCALE } from "@/shared/utils/locale.utils";

export default function AddCandidate() {
  const { client_id: clientId, assessment_id: assessmentId } = useParams<{
    client_id: string;
    assessment_id: string;
  }>();
  useValidateClientAccessHook(String(clientId));

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleInviteCandidate = async (value: { candidatesData: string[] }) => {
    setIsLoading(true);

    try {
      const candidatesCsvFile = await CandidateUtils.fromTextToCsvCandidates(value.candidatesData);

      await AssessmentHttpService.inviteToAssessment(Number(assessmentId), candidatesCsvFile);
      notification.success({
        message: (
          <>
            <p>Invites sent. Invites might take a few seconds to process.</p>
          </>
        ),
      });

      setTimeout(() => {
        navigate(`/${CLIENT_ROUTE}/${clientId}/${ASSESSMENT_ROUTE}/${assessmentId}/${ASSESSMENT_EDIT_INVITES_ROUTE}`);
      }, 3000);
    } catch {
      notification.error({ message: "There was an error inviting candidates." });
    } finally {
      setIsLoading(false);
    }
  };
  const { assessment } = useContext(AssessmentContext);

  const assessmentAbility = useContext(AssessmentAbilityContext);
  const canCreateAssessmentInvite = assessmentAbility.can(Action.Create, Subject.ASSESSMENT_INVITE);

  return (
    <>
      <SEO titleParams={[assessment?.name[DEFAULT_LOCALE], "Invite Candidates"]} />

      <AddCandidateForm
        isLoading={isLoading}
        disabled={!canCreateAssessmentInvite}
        title="Invite Candidates"
        formName="inviteCandidates"
        onSubmit={handleInviteCandidate}
        inputProps={[
          {
            name: "candidatesData",
            label: "To",
            errorText: "You need at least one candidate email",
            required: true,
          },
        ]}
        buttonProps={{
          title: "Send invite",
        }}
      />
    </>
  );
}
